import { type ReactElement } from 'react';
import { Box, Card, CardContent, Skeleton, Text, TrackingIcon } from '@citibox-panel/ui';
import { ReadErrorStrip } from '../../../components/ReadErrorStrip';
import { formatCount, formatPercentage } from '../lib/format';
import type { QrBatchStats } from '../lib/qrBatch';

export interface StatsStripProps {
  stats: QrBatchStats | undefined;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
}

// TODO: i18n — copy en ES hasta Fase 1.
const READ_ERROR = 'No se han podido cargar las cifras de códigos.';

// Three numbers and a line: how many codes exist, how many are on a lock, and how many are left. A
// strip, not a dashboard — the person came here to ask for labels, and this only says whether there
// is room for them.
export function StatsStrip({ stats, isPending, isError, onRetry }: StatsStripProps): ReactElement {
  const figures =
    stats === undefined
      ? []
      : [
          { label: 'Generados', value: stats.generated },
          { label: 'En uso', value: stats.inUse },
          { label: 'Restantes', value: stats.remaining },
        ];

  return (
    <Card variant="elevation" elevation={4} sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrackingIcon fontSize="small" />
          <Text variant="subtitle2" component="h3" bold>
            Códigos LOCK1
          </Text>
        </Box>
        {isError && <ReadErrorStrip message={READ_ERROR} onRetry={onRetry} />}
        {!isError && isPending && (
          <Box aria-label="Cargando las cifras de códigos">
            <Skeleton variant="text" height={48} />
          </Box>
        )}
        {!isError && stats !== undefined && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
              {figures.map((figure) => (
                <Box key={figure.label}>
                  <Text variant="overline" component="p" bold dense>
                    {figure.label}
                  </Text>
                  <Text variant="h6" component="p" bold>
                    {formatCount(figure.value)}
                  </Text>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 1 }}>
              <Text variant="body2">
                {`${formatPercentage(stats.spaceUsedPercentage)} del espacio de códigos usado`}
              </Text>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
